// McpScanReport — the "Agentic Scan" report (overview · lifecycle · findings ·
// trust · logs · json), redesigned to Zeus-crisp density in corenet/Agate tokens:
// a connected stat-strip, caps ▍ section headers, each section in a contained Panel
// with dense line-by-line tables inside, monospace identifiers, near-monochrome.
//
// Presentation only — the view model (scanReport.ts) is unchanged, and the honesty
// properties hold: completion is never shown as a pass, finding counts always
// reconcile (vulnerable + secure + inconclusive + n/a = total), adjudicated
// severity is shown, and cards for absent data hide rather than showing "—".
import { useMemo, useState } from "react";
import { Tabs, Typography, Table, theme, Empty, Button, ConfigProvider } from "antd";
import type { TableColumnsType } from "antd";
import { ExternalLink, Info, ShieldCheck } from "lucide-react";
import { SeverityText, HintIcon } from "@/components/agate";
import {
  Panel,
  SectionHeader,
  RailHeader,
  DefTable,
  Field,
  ToneTag,
  ScoreMeter,
  StatStrip,
  Th,
  type StatTile,
} from "@/components/mcpScan/report/kit";
import LifecycleBar from "@/components/mcpScan/report/LifecycleBar";
import FindingsTable from "@/components/mcpScan/report/FindingsTable";
import ComponentTools from "@/components/mcpScan/report/ComponentTools";
import ComponentsStrip from "@/components/mcpScan/report/ComponentsStrip";
import ApproveScanModal from "@/components/mcpScan/ApproveScanModal";
import type { ScanResult } from "@/lib/api/scans";
import {
  fmtDuration,
  fmtUsd,
  severityToTone,
  tierToTone,
  type ScanReportVM,
  type VMComponent,
  type VMFinding,
  type Tone,
} from "@/lib/api/scanReport";

const { Text, AntLink } = { Text: Typography.Text, AntLink: Typography.Link };
const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

const HOT_KINDS = ["destructive", "exec_capable", "secrets_access", "iam_admin", "network_broad", "untrusted_output"];

function toneClass(t: Tone): string {
  return t === "danger"
    ? "text-danger"
    : t === "warning"
      ? "text-warning"
      : t === "success"
        ? "text-success"
        : t === "info"
          ? "text-info"
          : "text-text-primary";
}

function TrustPill({ score, tier }: { score?: number; tier?: string }) {
  if (score == null) return <ToneTag tone="muted">not scored</ToneTag>;
  return (
    <ToneTag tone={tierToTone(tier)} title="Repository trust score. Higher means lower assessed risk. Advisory only.">
      {score}/100{tier ? ` (${tier})` : ""}
    </ToneTag>
  );
}

function ProseList({ items }: { items: string[] }) {
  if (!items.length) return <div className="text-sm text-text-muted">None recorded.</div>;
  return (
    <ul className="m-0 flex list-disc flex-col gap-2 pl-5">
      {items.map((t, i) => (
        <li key={i} className="text-sm leading-relaxed text-text-secondary">
          {t}
        </li>
      ))}
    </ul>
  );
}

function humanType(t: string): string {
  if (t === "mcp-server") return "MCP server";
  if (!t) return "component";
  return t.charAt(0).toUpperCase() + t.slice(1);
}
function humanEnum(s: string): string {
  return (s || "").replace(/[_-]/g, " ");
}

// ── overview tab ─────────────────────────────────────────────────────────────
function OverviewTab({ vm }: { vm: ScanReportVM }) {
  const [selId, setSelId] = useState(
    vm.components.find((c) => c.type === "mcp-server")?.id ?? vm.components[0]?.id ?? "",
  );
  const sel: VMComponent | undefined = useMemo(
    () => vm.components.find((c) => c.id === selId) ?? vm.components[0],
    [vm.components, selId],
  );

  const hasLifecycle = vm.lifecycle.segments.length > 0;
  const hasEval = vm.engines.length > 0 || !!vm.evalPack.source;

  const mcpTools = useMemo(() => vm.components.reduce((n, c) => n + c.toolsCount, 0), [vm.components]);
  const hotTools = useMemo(
    () =>
      vm.components.reduce(
        (n, c) => n + HOT_KINDS.reduce((m, k) => m + (c.toolTypeCounts[k] ?? 0), 0),
        0,
      ),
    [vm.components],
  );

  const gc = vm.globalFindingCounts;
  const tiles: StatTile[] = [
    {
      label: "Trust score",
      emphasized: true,
      hint: "0 to 100. Higher means lower assessed risk. Tier runs critical, low, medium, high (the opposite direction from finding severity, where low is best). Advisory.",
      value: vm.repoTrust.score != null ? `${vm.repoTrust.score}/100` : "—",
      subLabel: vm.repoTrust.tier ? `${vm.repoTrust.tier} · higher is safer` : "advisory",
      toneClass: toneClass(tierToTone(vm.repoTrust.tier)),
    },
    {
      label: "Worst severity",
      value: <span className={`uppercase ${toneClass(severityToTone(vm.worstSeverity))}`}>{vm.worstSeverity || "—"}</span>,
      subLabel: "adjudicated, across all findings",
    },
    {
      label: "Vulnerable",
      value: gc.vulnerable,
      subLabel: `of ${vm.globalFindings.length} findings · ${gc.unknown} inconclusive`,
      toneClass: gc.vulnerable > 0 ? "text-danger" : "text-text-primary",
    },
    { label: "Components", value: vm.totalComponents, subLabel: "servers · skills · code" },
    {
      label: "MCP tools",
      value: mcpTools,
      subLabel: hotTools > 0 ? `${hotTools} sensitive` : "capabilities mapped",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <StatStrip tiles={tiles} />

      {/* SUMMARY panel + separate IDENTITY / EVALUATION panels in the rail */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Panel>
          <SectionHeader label="Summary" />
          <DefTable
            rows={[
              {
                label: "Repo",
                value: vm.repoUrl ? (
                  <AntLink href={vm.repoUrl} target="_blank" rel="noreferrer">
                    {vm.repoUrl.replace(/^https?:\/\//, "")} <ExternalLink size={11} style={{ display: "inline" }} />
                  </AntLink>
                ) : (
                  "—"
                ),
              },
              { label: "Commit", value: <span style={{ fontFamily: MONO, fontSize: 12 }}>{vm.ref ? vm.ref.slice(0, 12) : "—"}</span> },
              {
                label: "Worst severity",
                value: <SeverityText tone={severityToTone(vm.worstSeverity)}>{vm.worstSeverity || "—"}</SeverityText>,
              },
              {
                label: "Trust score",
                value: (
                  <span className="inline-flex items-center gap-1.5">
                    <TrustPill score={vm.repoTrust.score} tier={vm.repoTrust.tier} />
                    <HintIcon hint="Higher means lower assessed risk. Tier runs critical, low, medium, high (the opposite of finding severity, where low is best)." />
                  </span>
                ),
              },
              { label: "Components", value: <span className="tabular-nums">{vm.totalComponents}</span> },
              {
                label: "Languages",
                value: (
                  <div className="flex flex-wrap gap-1">
                    {vm.languages.length ? vm.languages.map((l) => <ToneTag key={l} tone="muted">{l}</ToneTag>) : "—"}
                  </div>
                ),
              },
              ...(vm.transports.length > 0
                ? [
                    {
                      label: "Transports",
                      value: (
                        <div className="flex flex-wrap gap-1">
                          {vm.transports.map((t) => <ToneTag key={t} tone="muted">{t}</ToneTag>)}
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel>
            <RailHeader>Identity</RailHeader>
            <DefTable
              labelWidth={96}
              rows={[
                { label: "Scan ID", value: <span style={{ fontFamily: MONO, fontSize: 12 }}>{vm.scanId}</span> },
                {
                  label: "Tenant",
                  value: vm.tenant.isNew ? <ToneTag tone="info">anonymous / public</ToneTag> : <span>known tenant</span>,
                },
                ...(vm.tenant.id
                  ? [{ label: "Tenant ID", value: <span style={{ fontFamily: MONO, fontSize: 12 }}>{vm.tenant.id}</span> }]
                  : []),
                ...(vm.completedAt
                  ? [{ label: "Completed", value: new Date(vm.completedAt).toUTCString().replace("GMT", "UTC") }]
                  : []),
              ]}
            />
          </Panel>
          {hasEval && (
            <Panel>
              <RailHeader>Evaluation</RailHeader>
              <DefTable
                labelWidth={96}
                rows={[
                  ...(vm.evalPack.source ? [{ label: "Source", value: <ToneTag tone="muted">{vm.evalPack.source}</ToneTag> }] : []),
                  ...vm.engines.map((e) => ({
                    label: e.role,
                    value: (
                      <span>
                        <span style={{ fontFamily: MONO, fontSize: 12 }}>{e.model}</span>
                        {e.engine && <span className="ml-2 text-xs text-text-muted">{e.engine}</span>}
                      </span>
                    ),
                  })),
                  ...(vm.rulesetSha
                    ? [{ label: "Ruleset", value: <span style={{ fontFamily: MONO, fontSize: 12 }}>{vm.rulesetSha.slice(0, 12)}</span> }]
                    : []),
                ]}
              />
            </Panel>
          )}
        </div>
      </div>

      {hasLifecycle && (
        <Panel>
          <SectionHeader label="Lifecycle" />
          <LifecycleBar lifecycle={vm.lifecycle} />
        </Panel>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel>
          <SectionHeader label="High-level risks" />
          <ProseList items={vm.highLevelRisks} />
        </Panel>
        <Panel>
          <SectionHeader label="Top remediations" />
          <ProseList items={vm.topRemediations} />
        </Panel>
      </div>

      <Panel>
        <SectionHeader label="Global findings" hint="Repository-level findings that apply across every component." />
        <FindingsTable findings={vm.globalFindings} />
      </Panel>

      <Panel>
        <SectionHeader
          label="Components"
          hint="Each MCP server, skill, or agentic-code unit scanned. Select one for its findings and tools. F = findings · T = tools; the number is that component's trust score."
          extra={`${vm.components.length} scanned · F = findings · T = tools`}
        />
        <ComponentsStrip components={vm.components} selectedId={sel?.id ?? ""} onSelect={setSelId} />
      </Panel>

      {sel && sel.findings.length > 0 && (
        <Panel>
          <SectionHeader label="Component findings" extra={`${humanType(sel.type)} · ${sel.name}`} />
          <FindingsTable findings={sel.findings} />
        </Panel>
      )}
      {sel && (
        <Panel>
          <SectionHeader
            label="Component tools"
            hint="The tools this component exposes and the capabilities each can exercise. Trust is 0–100 (higher = lower assessed risk)."
            extra={`${sel.name} · trust ${sel.trustScore ?? "—"}/100`}
          />
          {sel.tools.length ? (
            <ComponentTools tools={sel.tools} typeCounts={sel.toolTypeCounts} />
          ) : (
            <Empty description="This component exposes no tools." image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Panel>
      )}
    </div>
  );
}

// ── lifecycle tab ─────────────────────────────────────────────────────────────
function LifecycleTab({ vm }: { vm: ScanReportVM }) {
  const hasLifecycle = vm.lifecycle.segments.length > 0;
  const hasCost = vm.cost.total > 0;
  if (!hasLifecycle && !hasCost)
    return <Empty description="No lifecycle timing or cost recorded for this scan." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <div className="flex flex-col gap-4">
      {hasLifecycle && (
        <Panel>
          <SectionHeader label="Stage timeline" />
          <LifecycleBar lifecycle={vm.lifecycle} />
          <div className="mt-4 text-sm text-text-secondary">
            Total processing time{" "}
            <span className="font-semibold tabular-nums text-text-primary">{fmtDuration(vm.lifecycle.totalMs)}</span>{" "}
            <HintIcon hint="Sum of scanning, adjudication, and reconciliation. Not elapsed wall-clock, which includes queue gaps between stages." />
          </div>
        </Panel>
      )}
      {hasCost && (
        <Panel>
          <SectionHeader label="Cost breakdown" />
          <div className="grid grid-cols-2 gap-x-10 sm:grid-cols-4">
            <Field label="Scan">
              <span className="tabular-nums">{fmtUsd(vm.cost.scan)}</span>
            </Field>
            <Field label="Adjudication">
              <span className="tabular-nums">{fmtUsd(vm.cost.adjudication)}</span>
            </Field>
            <Field label="Reconciliation">
              <span className="tabular-nums">{fmtUsd(vm.cost.reconciliation)}</span>
            </Field>
            <Field label="Total">
              <span className="font-semibold tabular-nums">{fmtUsd(vm.cost.total)}</span>
            </Field>
          </div>
        </Panel>
      )}
    </div>
  );
}

// ── trust tab ─────────────────────────────────────────────────────────────────
function TrustTab({ vm }: { vm: ScanReportVM }) {
  const cols: TableColumnsType<VMComponent> = [
    { title: Th("Component"), key: "name", render: (_, c) => <Text className="text-sm">{c.name}</Text> },
    { title: Th("Type"), key: "type", width: 130, render: (_, c) => <ToneTag tone="muted">{humanType(c.type)}</ToneTag> },
    { title: Th("Findings"), key: "f", width: 100, render: (_, c) => <Text className="tabular-nums">{c.findingsCount}</Text> },
    { title: Th("Tools"), key: "t", width: 90, render: (_, c) => <Text className="tabular-nums">{c.toolsCount}</Text> },
    {
      title: Th("Trust"),
      key: "trust",
      width: 160,
      sorter: (a, b) => (a.trustScore ?? -1) - (b.trustScore ?? -1),
      render: (_, c) => <ScoreMeter score={c.trustScore} tone={tierToTone(c.trustTier ?? undefined)} />,
    },
  ];
  return (
    <div className="flex flex-col gap-4">
      <Panel>
        <SectionHeader label="Repository trust" hint="Trust is 0–100; higher = lower assessed risk." />
        <div className="flex items-center gap-4">
          <TrustPill score={vm.repoTrust.score} tier={vm.repoTrust.tier} />
          <Text className="text-sm text-text-secondary">
            Worst severity <SeverityText tone={severityToTone(vm.worstSeverity)}>{vm.worstSeverity}</SeverityText> across{" "}
            {vm.totalComponents} components.
          </Text>
        </div>
      </Panel>
      <Panel>
        <SectionHeader label="Per-component trust" extra="lowest trust first" />
        <Table<VMComponent>
          size="small"
          rowKey={(c) => c.id}
          dataSource={[...vm.components].sort((a, b) => (a.trustScore ?? -1) - (b.trustScore ?? -1))}
          columns={cols}
          scroll={{ x: 560 }}
          pagination={vm.components.length > 20 ? { pageSize: 20, size: "small" } : false}
        />
      </Panel>
    </div>
  );
}

// ── logs tab ──────────────────────────────────────────────────────────────────
function LogsTab({ vm }: { vm: ScanReportVM }) {
  const hasAny = vm.engines.length || vm.workerVersions.length || vm.callback.status;
  if (!hasAny) return <Empty description="No engine, version, or callback records for this scan." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <div className="flex flex-col gap-4">
      {vm.engines.length > 0 && (
        <Panel>
          <SectionHeader label="Engines" />
          {vm.engines.map((e) => (
            <Field key={e.role} label={e.role}>
              <span style={{ fontFamily: MONO, fontSize: 12 }}>{e.model}</span>
              {e.engine && <span className="ml-2 text-xs text-text-muted">{e.engine}</span>}
            </Field>
          ))}
        </Panel>
      )}
      {vm.workerVersions.length > 0 && (
        <Panel>
          <SectionHeader label="Worker versions" />
          {vm.workerVersions.map((w) => (
            <Field key={w.name} label={w.name}>
              <span style={{ fontFamily: MONO, fontSize: 12 }}>
                {w.version} {w.commit && <span className="text-text-muted">· {w.commit.slice(0, 10)}</span>}
              </span>
            </Field>
          ))}
        </Panel>
      )}
      <Panel>
        <SectionHeader label="Callback" hint="Where results were posted, if this scan was requested via API." />
        <Field label="Status">
          <ToneTag tone={vm.callback.status === "not_required" ? "muted" : "info"}>{humanEnum(vm.callback.status)}</ToneTag>
        </Field>
        <Field label="Target">{vm.callback.target || "—"}</Field>
      </Panel>
    </div>
  );
}

// ── json tab ──────────────────────────────────────────────────────────────────
function JsonTab({ raw }: { raw: unknown }) {
  const { token } = theme.useToken();
  const text = useMemo(() => JSON.stringify(raw, null, 2), [raw]);
  return (
    <Panel>
      <SectionHeader label="Raw scan result" />
      <pre
        className="m-0 max-h-[70vh] overflow-auto rounded-lg p-4"
        style={{ background: token.colorBgLayout, fontFamily: MONO, fontSize: 12, lineHeight: 1.6 }}
      >
        {text}
      </pre>
    </Panel>
  );
}

export default function McpScanReport({
  vm,
  raw,
  scanResult,
  isAdmin,
  onApproved,
}: {
  vm: ScanReportVM;
  raw?: unknown;
  // When provided (live queue → report), an admin can approve/acknowledge the scan,
  // mirroring the MCP Scan flow (POST /scans/{id}/approve). Omitted in the fixture preview.
  scanResult?: ScanResult;
  isAdmin?: boolean;
  onApproved?: () => void;
}) {
  const { token } = theme.useToken();
  const [approveOpen, setApproveOpen] = useState(false);
  const repoName = vm.repoUrl
    ? vm.repoUrl.replace(/^https?:\/\//, "").replace(/^github\.com\//, "")
    : vm.scanId;
  const canApprove = !!isAdmin && !!scanResult && scanResult.status === "done";

  const allFindings: VMFinding[] = useMemo(
    () => [...vm.globalFindings, ...vm.components.flatMap((c) => c.findings)],
    [vm.globalFindings, vm.components],
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: "transparent",
            headerColor: token.colorTextTertiary,
            headerSplitColor: "transparent",
            borderColor: token.colorBorderSecondary,
            cellPaddingBlock: 10,
            cellPaddingInline: 12,
            rowHoverBg: token.colorFillQuaternary,
            fontSize: 13,
          },
        },
      }}
    >
      <div className="flex flex-col gap-4">
        {/* Enterprise header banner: branded gradient, repo-leads title, trust +
            approve action, advisory footer strip. */}
        <header className="overflow-hidden rounded-xl border border-border">
          <div
            className="px-6 py-5"
            style={{ background: `linear-gradient(135deg, ${token.colorPrimaryBg} 0%, ${token.colorBgContainer} 60%)` }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.7px] text-text-muted">Agentic Scan</div>
                <h1 className="mt-1 truncate font-mono text-[22px] font-semibold leading-tight text-text-primary" title={repoName}>
                  {repoName}
                </h1>
                <p className="mt-2 max-w-3xl text-sm text-text-secondary">
                  Reviews the MCP servers, skills, and agentic code in this repository. Each finding includes the
                  evidence behind it and how to remediate it.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {canApprove && (
                  <Button type="primary" icon={<ShieldCheck size={15} />} onClick={() => setApproveOpen(true)}>
                    Approve…
                  </Button>
                )}
                <TrustPill score={vm.repoTrust.score} tier={vm.repoTrust.tier} />
              </div>
            </div>
          </div>
          <div
            className="flex items-start gap-2 border-t border-border px-6 py-2.5"
            style={{ background: token.colorInfoBg }}
          >
            <Info size={15} style={{ color: token.colorInfo, marginTop: 1, flex: "none" }} />
            <span className="text-[13px] text-text-secondary">
              <b className="font-semibold text-text-primary">This report is advisory.</b> It informs your decision and
              does not enforce or block anything at runtime.
            </span>
          </div>
        </header>

        <Tabs
          defaultActiveKey="overview"
          items={[
            { key: "overview", label: "Overview", children: <OverviewTab vm={vm} /> },
            { key: "lifecycle", label: "Lifecycle", children: <LifecycleTab vm={vm} /> },
            {
              key: "findings",
              label: "Findings",
              children: (
                <Panel>
                  <SectionHeader label="All findings" hint="Repository-level findings plus every component's own findings." />
                  <FindingsTable findings={allFindings} />
                </Panel>
              ),
            },
            { key: "trust", label: "Trust", children: <TrustTab vm={vm} /> },
            { key: "logs", label: "Logs", children: <LogsTab vm={vm} /> },
            { key: "json", label: "JSON", children: <JsonTab raw={raw ?? vm} /> },
          ]}
        />
      </div>

      {scanResult && (
        <ApproveScanModal
          open={approveOpen}
          onClose={() => setApproveOpen(false)}
          result={scanResult}
          onApproved={() => {
            setApproveOpen(false);
            onApproved?.();
          }}
        />
      )}
    </ConfigProvider>
  );
}
